import { HttpErrorResponse } from '@angular/common/http';
import {
  QuickSizerErrorKind,
  QuickSizerErrorSeverity,
  QuickSizerStepError,
  WorkloadPool,
} from '../../feature-modules/quick-sizer/models/quick-sizer.models';

/** One message per kind: each failure has a different fix. */
export const ERROR_MESSAGE_KEYS: Record<QuickSizerErrorKind, string> = {
  VSAN_OVERHEAD_EXCEEDS_CPU:
    "vSAN's own CPU use, plus the CPU reserves, needs more cores than these hosts have, so the cluster cannot run vSAN at all.",
  VSAN_OVERHEAD_EXCEEDS_MEMORY:
    "vSAN's own memory footprint is larger than the RAM in these hosts, so the cluster cannot run vSAN at all.",
  VSAN_OVERHEAD_EXCEEDS_STORAGE:
    "vSAN's own storage overheads consume the capacity drives, leaving nothing for data.",
  UNDERSIZED_FOR_VCF:
    'vSAN fits on this hardware, but the selected VCF components do not.',
  REFERENCE_DATA_MISSING:
    'Quick Sizer has no IOPS reference data for this hardware combination, so it cannot size the cluster.',
  NO_WORKLOAD_CAPACITY: 'No VM fits on this cluster: vSAN and VCF overheads leave nothing for workloads.',
  CONFIGS_FAILED: 'Could not load Quick Sizer configuration. Calculate is unavailable until this succeeds.',
  VALIDATION: 'Some inputs are outside the range Quick Sizer accepts.',
  UNKNOWN: 'Quick Sizer could not calculate a recommendation.',
};

export const ERROR_REMEDIES: Record<QuickSizerErrorKind, string> = {
  VSAN_OVERHEAD_EXCEEDS_CPU: 'Use more CPU cores per host, then calculate again.',
  VSAN_OVERHEAD_EXCEEDS_MEMORY: 'Increase memory per host, then calculate again.',
  VSAN_OVERHEAD_EXCEEDS_STORAGE: 'Use larger capacity drives, add drives per host, or add hosts.',
  UNDERSIZED_FOR_VCF: 'Add hosts, use larger drives, or increase memory per host.',
  REFERENCE_DATA_MISSING:
    'Choose a different ReadyNode or drive configuration. Re-entering the same values will not help.',
  NO_WORKLOAD_CAPACITY: 'Add hosts, use larger drives, or increase memory per host.',
  CONFIGS_FAILED: 'Reload the page. If it keeps failing, the sizing service is down.',
  VALIDATION: 'Review the highlighted fields and try again.',
  UNKNOWN: 'Try again. If it keeps failing, please click on the Feedback button to raise the issue with the sizing team.',
};

const ERROR_SEVERITY: Record<QuickSizerErrorKind, QuickSizerErrorSeverity> = {
  VSAN_OVERHEAD_EXCEEDS_CPU: 'hard',
  VSAN_OVERHEAD_EXCEEDS_MEMORY: 'hard',
  VSAN_OVERHEAD_EXCEEDS_STORAGE: 'hard',
  UNDERSIZED_FOR_VCF: 'hard',
  REFERENCE_DATA_MISSING: 'hard',
  NO_WORKLOAD_CAPACITY: 'hard',
  CONFIGS_FAILED: 'hard',
  VALIDATION: 'soft',
  UNKNOWN: 'hard',
};

/** Kinds whose fix is on the hardware step. */
const HARDWARE_KINDS: ReadonlySet<QuickSizerErrorKind> = new Set([
  'VSAN_OVERHEAD_EXCEEDS_CPU',
  'VSAN_OVERHEAD_EXCEEDS_MEMORY',
  'VSAN_OVERHEAD_EXCEEDS_STORAGE',
  'UNDERSIZED_FOR_VCF',
  'REFERENCE_DATA_MISSING',
  'NO_WORKLOAD_CAPACITY',
]);

/** Kinds whose message quotes a shortfall amount. */
const SHORTFALL_KINDS: ReadonlySet<QuickSizerErrorKind> = new Set([
  'VSAN_OVERHEAD_EXCEEDS_CPU',
  'VSAN_OVERHEAD_EXCEEDS_MEMORY',
  'VSAN_OVERHEAD_EXCEEDS_STORAGE',
  'UNDERSIZED_FOR_VCF',
  'NO_WORKLOAD_CAPACITY',
]);

interface ApiErrorBody {
  id?: string;
  code?: number;
  message?: string;
  /** Which of CPU/MEMORY/STORAGE the backend found short. */
  shortResources?: string[];
}

/** Per-host fixes: vSAN takes CPU, RAM and disk on every host, so adding hosts can widen a shortfall. */
const SHORT_RESOURCE_FIXES: Record<string, string> = {
  CPU: 'use more CPU cores per host',
  MEMORY: 'increase memory per host',
  STORAGE: 'use larger drives',
};

/** Remedy naming only the short resources, or `undefined` when none were sent. */
function remedyForShortResources(
  shortResources: string[] | undefined,
  componentsSelected = true,
): string | undefined {
  const fixes = (shortResources ?? [])
    .map(resource => SHORT_RESOURCE_FIXES[resource])
    .filter((fix): fix is string => !!fix);
  if (fixes.length === 0) {
    return undefined;
  }
  const sentence = componentsSelected
    ? [...fixes, 'or drop components'].join(', ')
    : fixes.length > 1 ? `${fixes.slice(0, -1).join(', ')} or ${fixes.at(-1)}` : fixes[0];
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}

function kindFromId(id: string | undefined): QuickSizerErrorKind {
  switch (id) {
    case 'VSAN_OVERHEAD_EXCEEDS_CPU':
    case 'VSAN_OVERHEAD_EXCEEDS_MEMORY':
    case 'VSAN_OVERHEAD_EXCEEDS_STORAGE':
    case 'UNDERSIZED_FOR_VCF':
    case 'REFERENCE_DATA_MISSING':
    case 'NO_WORKLOAD_CAPACITY':
      return id;
    default:
      return 'UNKNOWN';
  }
}

function stepFor(kind: QuickSizerErrorKind): QuickSizerStepError['step'] {
  if (HARDWARE_KINDS.has(kind)) {
    return 'hardware';
  }
  if (kind === 'VALIDATION' || kind === 'CONFIGS_FAILED') {
    return 'storage';
  }
  return 'generic';
}

/** A decimal, but not part of a version like `9.1.0`. */
const DECIMAL_NUMBER = /(?<![\d.])\d+\.\d+(?!\.?\d)/g;

/** Rounds shortfall decimals up (`2376.61 GB` → `2377 GB`). */
function roundShortfallAmounts(kind: QuickSizerErrorKind, message: string | undefined): string | undefined {
  if (!message || !SHORTFALL_KINDS.has(kind)) {
    return message;
  }
  return message.replace(DECIMAL_NUMBER, amount => String(Math.ceil(Number(amount))));
}

/** Routes Quick Sizer HTTP failures by `Error.id`, never by message text. */
export function parseQuickSizerHttpError(error: HttpErrorResponse): QuickSizerStepError {
  const first = Array.isArray(error.error) ? (error.error[0] as ApiErrorBody | undefined) : undefined;
  let kind: QuickSizerErrorKind;
  if (error.status === 400) {
    kind = 'VALIDATION';
  } else if (error.status === 422 || error.status === 500) {
    kind = kindFromId(first?.id);
  } else {
    kind = 'UNKNOWN';
  }
  return {
    step: stepFor(kind),
    kind,
    severity: ERROR_SEVERITY[kind],
    messageKey: ERROR_MESSAGE_KEYS[kind],
    // Dropping components only helps when components caused the shortfall
    remedy: remedyForShortResources(first?.shortResources, kind === 'UNDERSIZED_FOR_VCF') ?? ERROR_REMEDIES[kind],
    detail: roundShortfallAmounts(kind, first?.message),
  };
}

export function configsFailedError(): QuickSizerStepError {
  return {
    step: 'storage',
    kind: 'CONFIGS_FAILED',
    severity: ERROR_SEVERITY.CONFIGS_FAILED,
    messageKey: ERROR_MESSAGE_KEYS.CONFIGS_FAILED,
    remedy: ERROR_REMEDIES.CONFIGS_FAILED,
  };
}

const POOL_NAMES: Record<WorkloadPool, string> = { STORAGE: 'storage', MEMORY: 'RAM', CPU: 'cores' };

/** A 200 that leaves a workload pool at 0. Reported like a rejection, on the hardware step. */
export function noWorkloadCapacityError(emptyPools: WorkloadPool[]): QuickSizerStepError {
  const names = emptyPools.map(pool => POOL_NAMES[pool]);
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names.at(-1)}` : names[0];
  return {
    step: 'hardware',
    kind: 'NO_WORKLOAD_CAPACITY',
    severity: ERROR_SEVERITY.NO_WORKLOAD_CAPACITY,
    messageKey: ERROR_MESSAGE_KEYS.NO_WORKLOAD_CAPACITY,
    remedy: remedyForShortResources(emptyPools) ?? ERROR_REMEDIES.NO_WORKLOAD_CAPACITY,
    detail: `No ${list} left for workloads.`,
  };
}
